"use client";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api/api";
import { useAuthenticated } from "@gettactic/hooks";
import { useTeamAttendanceFilters } from "@/features/teams/components/TeamSchedule/hooks/useTeamAttendanceFilters";
import { format, parseISO } from "date-fns";
import type { INewsItem } from "@gettactic/api";
import { TextEditorContent } from "@/ui/index";
import { cn } from "@gettactic/helpers";

const ThumbTackIcon = ({ className }: { className: string }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="currentColor"
      aria-label="Pinned"
      role="img"
    >
      <path d="M 14.490234 2.4902344 A 1.0001 1.0001 0 0 0 13.792969 4.2070312 L 14.388672 4.8027344 L 8.1894531 9.7753906 L 7.2070312 8.7929688 A 1.0001 1.0001 0 0 0 6.4902344 8.4902344 A 1.0001 1.0001 0 0 0 5.7929688 10.207031 L 9.09375 13.507812 L 3.2890625 19.310547 C 2.9020625 19.697547 2.9020625 20.323937 3.2890625 20.710938 C 3.6760625 21.097938 4.3024531 21.097938 4.6894531 20.710938 L 10.492188 14.90625 L 13.792969 18.207031 A 1.0001 1.0001 0 1 0 15.207031 16.792969 L 14.310547 15.896484 L 19.214844 9.6289062 L 19.792969 10.207031 A 1.0001 1.0001 0 1 0 21.207031 8.7929688 L 15.207031 2.7929688 A 1.0001 1.0001 0 0 0 14.490234 2.4902344 z" />
    </svg>
  );
};

const borderClass =
  "transition-all duration-300 border-1 border-neutral-200 hover:shadow-md";
// const hoverClass = "hover:border-1 hover:border-neutral-500 hover:shadow-md";

const NewsItemSkeleton = () => (
  <div className={cn("group/card flex flex-col", "rounded-2xl")}>
    <div
      className={cn(
        "border-1 border-transparent bg-background text-sm",
        "p-3",
        "rounded-xl",
        "animate-pulse"
      )}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <div className="h-5 w-3/4 bg-neutral-200 rounded mb-2" />
          <div className="h-4 w-48 bg-neutral-200 rounded" />
        </div>
        <div className="h-5 w-20 bg-neutral-200 rounded-full" />
      </div>
      <div className="mt-3 space-y-2">
        <div className="h-4 w-full bg-neutral-200 rounded" />
        <div className="h-4 w-5/6 bg-neutral-200 rounded" />
        <div className="h-4 w-4/6 bg-neutral-200 rounded" />
      </div>
    </div>
  </div>
);

export const News = () => {
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();

  const { filters: dashboardFilters } = useTeamAttendanceFilters({
    authenticatedUser,
    currentOffice,
    onlyFilters: true
  });
  const { data: newsData, isLoading } = useQuery({
    queryKey: [
      "organizations.news",
      currentOffice.id,
      dashboardFilters.offices?.[0]
    ],

    queryFn: async () => {
      const res = await api.client.organizations.news(
        dashboardFilters.offices?.[0] ?? null
      );
      return res.result;
    }
  });

  return (
    <div className="flex items-start justify-center w-full h-full">
      {isLoading ? (
        <div className="w-full max-w-4xl mx-auto space-y-3">
          {[1, 2, 3].map((i) => (
            <NewsItemSkeleton key={i} />
          ))}
        </div>
      ) : newsData?.elements && newsData.elements.length > 0 ? (
        <div className="w-full max-w-4xl mx-auto space-y-3">
          {newsData.elements.map((news) => (
            <OfficeNewsCard key={news.id} news={news} />
          ))}
        </div>
      ) : (
        <div className="w-full max-w-4xl mx-auto">
          <div className={cn("group/card flex flex-col", "rounded-2xl")}>
            <div
              className={cn(
                "border-1 border-neutral-200 bg-background text-sm",
                "p-6 md:p-8",
                "rounded-xl",
                "text-center"
              )}
            >
              <div className="max-w-md mx-auto">
                <div className="mb-4 flex justify-center">
                  <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-neutral-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                      />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  No news yet!
                </h3>
                <p className="text-sm text-muted-foreground">
                  We'll keep you in the loop once an admin on your account
                  publishes office news.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const OfficeNewsCard = ({ news }: { news: INewsItem }) => {
  return (
    <div className={cn("group/card flex flex-col", "rounded-2xl")}>
      <div
        className={cn(
          "border-1 border-transparent bg-background text-sm",
          "p-3",
          "rounded-xl",
          borderClass
        )}
      >
        <div className="flex justify-between items-start gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {news.is_pinned && (
                <ThumbTackIcon className="h-4 w-4 text-foreground flex-shrink-0" />
              )}
              <h3 className="font-semibold text-foreground line-clamp-2">
                {news.title}
              </h3>
            </div>
            <div className="text-xs text-muted-foreground">
              Updated {format(parseISO(news.updated), "PP")}
            </div>
          </div>
          {!news.office_id && (
            <div className="flex-shrink-0">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-foreground">
                All Offices
              </span>
            </div>
          )}
        </div>
        <div className="mt-3 text-sm text-foreground">
          <TextEditorContent value={news.body} />
        </div>
      </div>
    </div>
  );
};
